// =========================================================
// * Vue Material Dashboard - v1.4.0
// =========================================================
//
// * Product Page: https://www.creative-tim.com/product/vue-material-dashboard
// * Copyright 2019 Creative Tim (https://www.creative-tim.com)
// * Licensed under MIT (https://github.com/creativetimofficial/vue-material-dashboard/blob/master/LICENSE.md)
//
// * Coded by Creative Tim
//
// =========================================================
//
// * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import { createApp } from "vue";
import { createStore } from "vuex";
import { createRouter, createWebHistory } from "vue-router";
import App from "./App";
import { ISI_API_URL, TITLE_PREFIX } from "./config";

// Tag the browser tab with the environment (e.g. "[dev] iSi Admin Console")
// so dev/testing tabs can't be mistaken for production.
document.title = TITLE_PREFIX + document.title;

// router setup
import routes from "./routes/routes";

// Plugins
import GlobalComponents from "./globalComponents";
import GlobalDirectives from "./globalDirectives";
import Notifications from "./components/NotificationPlugin";
// import SidebarPlugin from "./components/SidebarPlugin";
import axios from "axios";
import { installAuthRefresh, startSessionKeepAlive } from "./utils/authRefresh";
import { installApiFailover } from "./utils/apiFailover";
import { isClientUser } from "./utils/clientAccess";

// The old ?auth_key= localStorage backdoor was removed in the auth refactor —
// it persisted an API key in the browser and bypassed session auth. Headless
// automation sends its API key in the Authorization header directly.
localStorage.removeItem("admin_auth_key");

// Sliding sessions: the isi_token lasts 7 days and every GET /user re-arms
// the full lifetime, so an active session never expires. The 403 interceptor
// heals a token that expired anyway and retries the failed request once; the
// keep-alive (started below, once the store exists) pings /user hourly for
// long-open tabs. See utils/authRefresh.js.
installAuthRefresh();

// If the main API pool is unreachable, retry against the standalone backup
// API host and stick to it until the primary recovers. See utils/apiFailover.js.
installApiFailover();

// Vuetify plugin
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import "./assets/css/id-mono.css";

import * as Chartist from "chartist";

// configure router
const router = createRouter({
  history: createWebHistory(),
  routes,
  linkExactActiveClass: "nav-item active",
});

const store = createStore({
  state: {
    user_data: { email: "", is_admin: false, isi_token: null },
    selected_user: { email: "" },
    user_list: [],
    authInitialized: false,
  },
  getters: {},
  mutations: {
    update_user_list(state, new_data) {
      state.user_list = new_data;
    },
    update_selected_user(state, new_data) {
      state.selected_user = new_data;
    },
    update_user_data(state, new_data) {
      state.user_data = new_data;
    },
    update_purchases(state, new_data) {
      state.purchases = new_data;
    },
    update_products(state, new_data) {
      state.products = new_data;
    },
    set_auth_initialized(state, value) {
      state.authInitialized = value;
    },
  },
  actions: {},
});

// Hourly session keep-alive — needs the store so refreshed user data
// (grants/admin flag) lands in Vuex too, not just the cookie.
startSessionKeepAlive(store);

// Navigation guard: admin-only routes, file-access routes, and the
// client-user restricted mode (non-admin with client_access grants gets the
// File Browser only). Returns the redirect path or null to allow.
function resolveNavigation(to) {
  const userData = store.state.user_data;
  const clientUser = isClientUser(userData);

  // Client users are locked to the /files pages (browser + editors).
  if (clientUser && !to.path.startsWith("/files")) {
    return "/files";
  }

  if (to.meta.requiresAdmin && !userData.is_admin) {
    return clientUser ? "/files" : "/dashboard";
  }

  if (to.meta.requiresFileAccess && !userData.is_admin && !clientUser) {
    return "/dashboard";
  }

  return null;
}

router.beforeEach((to, from, next) => {
  // If auth is not initialized yet, wait for it before deciding.
  if (!store.state.authInitialized) {
    const unwatch = store.watch(
      (state) => state.authInitialized,
      (authInitialized) => {
        if (authInitialized) {
          unwatch(); // Stop watching
          const redirect = resolveNavigation(to);
          if (redirect && redirect !== to.path) {
            next(redirect);
          } else {
            next();
          }
        }
      },
    );
    return; // Don't call next() yet
  }

  const redirect = resolveNavigation(to);
  if (redirect && redirect !== to.path) {
    next(redirect);
  } else {
    next();
  }
});

// Click/page-view telemetry (batched to isi_data POST /activity)
import { installTelemetry } from "./plugins/telemetry";
installTelemetry(router, store);

const vuetify = createVuetify({
  components,
  directives,
  theme: {
    defaultTheme: "light",
  },
});

const app = createApp(App);

app.config.globalProperties.$Chartist = Chartist;
app.config.globalProperties.$http = axios;

app.use(router);
app.use(store);
app.use(vuetify);
app.use(GlobalComponents);
app.use(GlobalDirectives);
app.use(Notifications);
// app.use(SidebarPlugin);

window.isi_v = app.mount("#app");
